/**
 * 发起退款申请 Modal（共享组件）
 *
 * Stream 2 Phase 2-2b / Design §5.2 A3 退款双人审批.
 *
 * 从 A3 退款管理页抽出为共享组件，便于 A2 订单详情页「支付信息」卡片
 * 直接发起退款（预填支付记录 ID / 金额 / 订单号 / 作品 ID），免去人工复制粘贴。
 *
 * 两个入口共用同一双人审批流（initiateRefund → pending_approval）：
 *  - A3 退款管理页：无预填，人工填写支付记录 ID
 *  - A2 订单详情页：带 prefill，支付记录 ID 锁定为只读，避免误改
 *
 * 权限：调用方负责用 PermissionGate REFUND_INITIATE 包裹触发按钮。
 */
import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Typography,
  message,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { initiateRefund } from '../api/refunds';

const { TextArea } = Input;
const { Text } = Typography;

/** 预填值（从订单详情进入时携带）。 */
export interface RefundPrefill {
  paymentRecordId?: string;
  /** 退款金额（元）。订单详情默认带入支付金额（分 / 100），可手动调小做部分退款。 */
  refundAmountYuan?: number;
  reason?: string;
  orderId?: string;
  workId?: string;
  isSubstitutePayment?: boolean;
  /** 锁定支付记录 ID 输入为只读（订单详情入口为 true，避免误改成别的支付记录）。 */
  lockPaymentRecordId?: boolean;
}

export interface InitiateRefundModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefill?: RefundPrefill;
}

export function InitiateRefundModal({
  open,
  onClose,
  onSuccess,
  prefill,
}: InitiateRefundModalProps) {
  const [form] = Form.useForm();
  const [isSubstitute, setIsSubstitute] = useState(false);

  // 打开时应用预填值；关闭时由 handleClose 清空。
  useEffect(() => {
    if (!open) return;
    setIsSubstitute(prefill?.isSubstitutePayment ?? false);
    form.setFieldsValue({
      paymentRecordId: prefill?.paymentRecordId,
      refundAmountYuan: prefill?.refundAmountYuan,
      reason: prefill?.reason,
      orderId: prefill?.orderId,
      workId: prefill?.workId,
      initiatorNote: undefined,
    });
    // 仅在开关切换时重置，prefill 引用稳定与否不影响首次打开
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return initiateRefund({
        paymentRecordId: v.paymentRecordId,
        refundAmount: Math.round((v.refundAmountYuan ?? 0) * 100),
        reason: v.reason,
        isSubstitutePayment: isSubstitute,
        orderId: v.orderId || undefined,
        workId: isSubstitute ? v.workId : undefined,
        initiatorNote: v.initiatorNote || undefined,
      });
    },
    onSuccess: () => {
      message.success('退款申请已发起');
      form.resetFields();
      setIsSubstitute(false);
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '发起退款失败');
    },
  });

  function handleClose() {
    form.resetFields();
    setIsSubstitute(false);
    onClose();
  }

  return (
    <Modal
      title="发起退款申请"
      open={open}
      onCancel={handleClose}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate());
      }}
      confirmLoading={mutation.isPending}
      okText="提交申请"
      data-testid="initiate-refund-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="paymentRecordId"
          label="支付记录 ID"
          rules={[{ required: true, message: '请输入支付记录 ID' }]}
          extra={
            prefill?.lockPaymentRecordId ? undefined : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                可在「打印订单 → 订单详情 → 支付信息」卡片中复制支付记录 ID
              </Text>
            )
          }
        >
          <Input
            placeholder="paymentRecordId"
            disabled={prefill?.lockPaymentRecordId}
            data-testid="payment-record-id-input"
          />
        </Form.Item>

        <Form.Item
          name="refundAmountYuan"
          label="退款金额（元）"
          rules={[
            { required: true, message: '请输入退款金额' },
            { type: 'number', min: 0.01, message: '金额必须大于 0' },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0.01}
            precision={2}
            placeholder="如 99.00"
            prefix="¥"
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="退款原因"
          rules={[
            { required: true, message: '请输入退款原因' },
            { min: 5, message: '退款原因不得少于 5 个字符' },
          ]}
        >
          <TextArea rows={3} placeholder="请详细描述退款原因（不少于 5 字）" />
        </Form.Item>

        <Form.Item label="是否替支付退款">
          <Switch
            checked={isSubstitute}
            onChange={(v) => {
              setIsSubstitute(v);
              if (!v) form.setFieldValue('workId', undefined);
            }}
            data-testid="is-substitute-switch"
          />
        </Form.Item>

        {isSubstitute && (
          <Form.Item
            name="workId"
            label="作品 ID"
            rules={[{ required: true, message: '替支付退款必须填写作品 ID' }]}
          >
            <Input placeholder="workId" data-testid="work-id-field" />
          </Form.Item>
        )}

        <Form.Item name="orderId" label="订单 ID（可选）">
          <Input placeholder="orderId（可不填）" />
        </Form.Item>

        <Form.Item name="initiatorNote" label="发起备注（可选）">
          <TextArea rows={2} placeholder="对审批人的说明（可不填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
